import { useState, useCallback } from 'react';
import { DEFAULT_OPTIONS } from '../../../../FlowchartView/TaskNode/DecisionOptionsModal';

/**
 * useDecisionState Hook
 * 
 * Manages state and handlers for decision nodes:
 * - Options modal visibility
 * - Answer selection
 * - Options editing (with auto-clear of invalid answers)
 * 
 * @param {object} task - The task object
 * @param {function} onAnswerChange - Callback when answer changes
 * @param {function} onOptionsChange - Callback when options change
 * @returns {object} Decision state and handlers
 */
export const useDecisionState = (task, onAnswerChange, onOptionsChange) => {
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  
  const isDecision = task.nodeType === 'decision';
  const options = task.options || DEFAULT_OPTIONS;
  const answer = task.answer || null;
  
  // Handle answer click - toggle selection
  const handleAnswerClick = useCallback((optionId) => (e) => {
    e.stopPropagation();
    onAnswerChange?.(task.id, optionId === answer ? null : optionId);
  }, [task.id, answer, onAnswerChange]);
  
  // Handle options change - clear answer if it's no longer valid
  const handleOptionsChange = useCallback((newOptions) => {
    onOptionsChange?.(task.id, newOptions);
    // If current answer is no longer valid, clear it
    if (answer && !newOptions.find(opt => opt.id === answer)) {
      onAnswerChange?.(task.id, null);
    }
  }, [task.id, answer, onAnswerChange, onOptionsChange]);
  
  return {
    isDecision,
    options,
    answer,
    showOptionsModal,
    setShowOptionsModal,
    handleAnswerClick,
    handleOptionsChange,
  };
};

export default useDecisionState;
